/**
 * Testcase component is responsible for rendering a table that displays the details
 * of test cases for a student's programming submission. It shows the input, output,
 * expected output, pass/fail status, and marks for each test case. Additionally,
 * it allows downloading the test case details and viewing error messages in a modal.

 * The component iterates over the testResults array and renders each test case's details
 * in a table row. If a test case has a runtime error , a clickable link
 * is provided to open a modal showing the detailed error messages. Additionally, there 
 * are icons to indicate pass/fail status and a button to download the test case details.
 */
import log from "loglevel";
// import * as React from 'react';
import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { IoMdDownload } from "react-icons/io";
import * as constants from "Constants";
import Modal from "react-bootstrap/Modal";
import "./TestCase.css";
import bodhibotlogo from "../../../../../../../assets/images/Bodhibotlogo.webp";
import { useDispatch } from "react-redux";
import { setChatbotExternalData } from "../../../../../../../Store/Chatbot/Action.js";

const mappedIcon = { 'false': '❌' || 'failed', 'true': '✅' || 'passed' }

function Testcase(props) {
  //state for showing pop upp screen for errors with the execution
  log.debug("TestCase.jsx: props", props)

  const [showPopup, setShowPopUP] = useState(0);
  const [error, setError] = useState(false);

  // if (props.testResults?.error_messages || props.testResults?.compiler_errors) {
  //   setError(true);
  // }

  // useEffect(() => {
  //   log.debug("TestCase.jsx: test results in testcase component", props.testResults)
  //   // iterate over results and check if any testcase has error messages or compiler errors, if yes then set error state to true
  //   let hasError = false;
  //   props.testResults?.forEach((testcase) => {
  //     if (testcase.error_messages?.length > 0 || testcase.compiler_errors?.length > 0) {
  //       hasError = true;
  //     }
  //   });
  //   setError(hasError);
  // }, [props.testResults])

  /**
   * Function to check if a testcase has error messages or compiler errors
   * @param {Object} testcase A json object containing testcase result
   * @returns `true` if error_message is present, else `false`
   */
  const checkForErrors = (testcase) => {
    if (testcase.error_messages?.length > 0 || testcase.compiler_errors?.length > 0) {
      return true;
    }
    return false;
  }

  const dispatch = useDispatch();

  const truncateText = (text, maxLines = 200) => {
      if (!text) return text;
      const lines = text.split('\n');
      if (lines.length > maxLines) {
          return lines.slice(0, maxLines).join('\n') + '\n... (output truncated)';
      }
      return text;
  };

  const handleTestcaseBodhibot = (row) => {
    let testcaseDeail = { "input": row.testcase_input };
    testcaseDeail["expected_output"] = row.testcase_output;
    testcaseDeail["actual_output"] = (row.student_output || '').slice(0, 100);
    const data = {
      type: "testcaseDetail",
      content: testcaseDeail,
      source: "testcase-row",
    };
    dispatch(setChatbotExternalData(data));
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ backgroundColor: 'white' }}>
        <Table
          sx={{
            minWidth: 650,
            backgroundColor: 'white',
            fontSize: "10px",
            '& .MuiTableCell-root': {
              backgroundColor: 'white',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 'bold',
            },
          }}
          size="small"
          aria-label="a dense table"
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Input</TableCell>
              <TableCell align="center">Actual Output</TableCell>
              <TableCell align="center">Expected Output</TableCell>
              <TableCell align="center">Marks</TableCell>
              <TableCell align="center">Status</TableCell>
              {/* {error &&
                <TableCell align="center">Error</TableCell>
              } */}
              <TableCell align="center">Errors</TableCell>
              <TableCell align="center">Download</TableCell>
              <TableCell align="center">Bodhibot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.testResults.map((row, i) => (
              <TableRow
                key={row.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  backgroundColor: 'white',
                }}
              >
                <TableCell component="th" scope="row" align="center">
                  {i + 1 || ' '}
                </TableCell>

                <TableCell align="center">
                  {row.testcase_name || '-'}
                </TableCell>

                <TableCell
                  align="left"
                >
                  <div className='m-2'>
                    <code className='code'>
                      {row.testcase_input || ' '}
                    </code>
                  </div>
                </TableCell>

                <TableCell
                  align="left"
                >
                  <div className='m-2'>
                    <code className='code'>
                      {truncateText(row.student_output) || 'nothing to display'}
                    </code>
                  </div>
                </TableCell>

                <TableCell
                  align="left"
                >
                  <div className='m-2'>
                    <code className='code'>
                      {truncateText(row.testcase_output) || ' '}
                    </code>
                  </div>
                </TableCell>

                <TableCell align="center">
                  {row.marks_obtained || '0'}
                </TableCell>

                <TableCell align="center">{mappedIcon[row.passed] || ' '}</TableCell>
                {/* {error &&
                  <TableCell align="center">
                    <OverlayTrigger overlay={<Tooltip>Click to Expand</Tooltip>}>
                      <a
                        variant="outline-secondary"
                        onClick={() => setShowPopUP(i + 1)}
                        style={{
                          color: "red",
                        }}
                      >
                        {row.error_messages || row.compiler_errors ? (
                          <u>Runtime Error</u>
                        ) : null}
                      </a>
                    </OverlayTrigger>
                  </TableCell>

                } */}
                <TableCell align="center">
                  {
                    checkForErrors(row) ? (
                      <Button
                        variant="outline-danger"
                        onClick={() => setShowPopUP(i + 1)}
                      >
                        {row.verdict === 'Time Limit Exceeded'
                          ? 'Time Limit Exceeded'
                          : 'Runtime Error'}
                      </Button>
                    ) : (
                      <span>-</span>
                    )
                  }
                </TableCell>
                <TableCell align="center">
                  <OverlayTrigger
                    overlay={<Tooltip>Download Testcase{i + 1}</Tooltip>}
                  >
                    <a
                      onClick={() =>
                        props.testcasesExportHandler(
                          constants.base_url +
                          "/evalpro/testcase/export_archive/" +
                          row.id,
                          "testcase" + row.id
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {" "}
                      <IoMdDownload size="20" />
                    </a>
                  </OverlayTrigger>
                </TableCell>
                <TableCell align="center">
                  <OverlayTrigger overlay={<Tooltip>Ask Bodhibot</Tooltip>}>
                    <img src={bodhibotlogo} style={{ borderRadius: "50%", height: "45px", border: "1px solid rgba(0, 0, 0, 0.2)" }} onClick={() => handleTestcaseBodhibot(row)} />
                  </OverlayTrigger>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        show={showPopup ? true : false}
        dialogClassName="modal-90w"
        onHide={() => setShowPopUP(0)}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>Error Messages</Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-12 col1">
              <textarea
                className="textarea1"
                value={props.testResults[showPopup - 1]?.error_messages}
                style={{
                  color: "red",
                  backgroundColor: "black",
                  width: "100%",
                  height: "200px",
                }}
                disabled
              ></textarea>
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </>
  );
}

export default Testcase;
